import { getMean, getStandardDeviation } from "@/lib/ai/calibration/ai-calibration-utils"
import {
	clamp,
	distanceFromPointToLine,
	getIntersection,
	getLineEquation,
	rad2Degree,
	rotatePoint,
	toFixed,
} from "@/utils/math"
import { vec2 } from "gl-matrix"
import { PixelData } from "@/core/utils/image-data"
import { startMeasurement } from "@/utils/benchmark"
import {
	angleDiff,
	detectLineSegmentRects,
	getCombinedLines,
	getLineSegments,
	Line,
	thetaToRotation,
} from "./ai-line-segment-detector"
import { radians } from "@/core/utils/math"
import { Adjustments } from "@/core/types"
import { logLineDetection } from "../utils"
// import { getWeightedSum } from "./ai-calibration-feature"
// import * as tf from "@/lib/ai/tf"

export interface ArrayData {
	data: number[] | Uint8Array
	width: number
	height: number
}

interface VanishingPoint {
	point: vec2 // [x, y]
	rotation: number
	aveError: number
	errors: number[]
	confidence: number
	stdX: number
	polarCoord: vec2 // [phi, lambda]
	lines: Line[]
	distE: number
}

const dThresh = 20
const rScale = 5 / 3

// Slower than cpu; result also slightly different
// async function sobelWithTFJS(imageData: PixelData) {
// 	console.time("tfjs imageToBitmap")
// 	let bitmap = await imageDataToBitmap(imageData)
// 	console.timeEnd("tfjs imageToBitmap")

// 	console.time("tfjs sobel")

// 	let imageTensor = tf.browser.fromPixels(bitmap).expandDims(0).toFloat() as tf.Tensor4D

// 	// Define Grayscale, Gaussian and Sobel kernels
// 	const grayScaleKernel = tf.tensor4d([0.3, 0.59, 0.11], [1, 1, 3, 1])

// 	const gaussianKernel = tf.tensor2d([
// 			[0.01257862, 0.02515723, 0.03144654, 0.02515723, 0.01257862],
// 			[0.02515723, 0.05660377, 0.0754717, 0.05660377, 0.02515723],
// 			[0.03144654, 0.0754717, 0.09433962, 0.0754717, 0.03144654],
// 			[0.02515723, 0.05660377, 0.0754717, 0.05660377, 0.02515723],
// 			[0.01257862, 0.02515723, 0.03144654, 0.02515723, 0.01257862],
// 		], [5, 5], 'float32').expandDims(-1).expandDims(-1) as tf.Tensor4D // 5,5,1,1

// 	const sobelKernelX = tf.tensor2d([
// 		[-1, 0, 1],
// 		[-2, 0, 2],
// 		[-1, 0, 1],
// 	], [3, 3], 'float32').expandDims(-1).expandDims(-1) as tf.Tensor4D

// 	const sobelKernelY = tf.tensor2d([
// 		[-1, -2, -1],
// 		[0, 0, 0],
// 		[1, 2, 1],
// 	], [3, 3], 'float32').expandDims(-1).expandDims(-1) as tf.Tensor4D

// 	// Apply Grayscale, Gaussian and Sobel filters

// 	const grayscaleImage = tf.conv2d(imageTensor, grayScaleKernel, 1, 'same')
// 	const imageGaussian = tf.conv2d(grayscaleImage, gaussianKernel, 1, 'same')
// 	const imageSobelX = tf.conv2d(imageGaussian, sobelKernelX, 1, 'same').squeeze()
// 	const imageSobelY = tf.conv2d(imageGaussian, sobelKernelY, 1, 'same').squeeze()

// 	const GXSquare = imageSobelX.square(); // Element-wise square of GX
// 	const GYSquare = imageSobelY.square(); // Element-wise square of GY
// 	const totalTensor = tf.sqrt(GXSquare.add(GYSquare))

// 	const GX = await imageSobelX.data()
// 	const GY = await imageSobelY.data()
// 	const total = await totalTensor.data()
// 	// logLineDetection("tfjs ", {GX, GY, total})
// 	console.timeEnd("tfjs sobel")

// 	return {total, GX, GY}
// }

export function bilinearInterpolation(imageData: ArrayData, newWidth: number, newHeight: number) {
	// const endMeasurement = startMeasurement("bilinearInterpolation")

	const { data, width, height } = imageData
	const newData = new Array(newWidth * newHeight)
	const scaleX = width / newWidth
	const scaleY = height / newHeight

	for (let y = 0; y < newHeight; y++) {
		for (let x = 0; x < newWidth; x++) {
			const px = x * scaleX
			const py = y * scaleY

			const x1 = Math.floor(px)
			const y1 = Math.floor(py)
			const x2 = Math.min(x1 + 1, width - 1)
			const y2 = Math.min(y1 + 1, height - 1)

			const q11 = data[y1 * width + x1] as number
			const q12 = data[y2 * width + x1] as number
			const q21 = data[y1 * width + x2] as number
			const q22 = data[y2 * width + x2] as number

			const dx1 = px - x1
			const dy1 = py - y1
			const dx2 = 1 - dx1
			const dy2 = 1 - dy1

			const newPixel = q11 * dx2 * dy2 + q21 * dx1 * dy2 + q12 * dx2 * dy1 + q22 * dx1 * dy1

			const i = y * newWidth + x
			newData[i] = newPixel
		}
	}
	// endMeasurement()

	return {
		data: newData,
		width: newWidth,
		height: newHeight,
	}
}

export function computeGaussianKernel(sigma: number, h: number) {
	const size = 2 * h + 1
	const gaussianKernel = Array<number>(size * size).fill(0)
	let totalSum = 0 // Used for normalization

	for (let y = -h; y <= h; y++) {
		for (let x = -h; x <= h; x++) {
			const value =
				(1 / (2 * Math.PI * sigma * sigma)) *
				Math.exp(-(x * x + y * y) / (2 * sigma * sigma))
			gaussianKernel[(y + h) * size + x + h] = value
			totalSum += value
		}
	}

	// Normalize the kernel so that its sum is 1
	if (totalSum !== 0) {
		for (let i = 0; i < size; i++) {
			for (let j = 0; j < size; j++) {
				const idx = i * size + j
				gaussianKernel[idx] = (gaussianKernel[idx] ?? 0) / totalSum
			}
		}
	}

	return gaussianKernel
}

export function grayScale(imageData: PixelData | ImageData): ArrayData {
	const original = imageData.data
	const width = imageData.width
	const height = imageData.height

	const output = new Array(width * height)

	// Convert to grayscale
	for (let i = 0; i < output.length; i++) {
		const ii = i * 4
		const R = original[ii] as number
		const G = original[ii + 1] as number
		const B = original[ii + 2] as number

		output[i] = 0.299 * R + 0.587 * G + 0.114 * B
	}

	return { data: output, width, height }
}

function gaussian(grayImage: ArrayData): ArrayData {
	// const endMeasurement = startMeasurement("grayScaleAndGaussian")
	const input = grayImage.data
	const width = grayImage.width
	const height = grayImage.height

	// const gaussKernel = [
	// 	0.01257862, 0.02515723, 0.03144654, 0.02515723, 0.01257862, 0.02515723, 0.05660377,
	// 	0.0754717, 0.05660377, 0.02515723, 0.03144654, 0.0754717, 0.09433962, 0.0754717, 0.03144654,
	// 	0.02515723, 0.05660377, 0.0754717, 0.05660377, 0.02515723, 0.01257862, 0.02515723,
	// 	0.03144654, 0.02515723, 0.01257862,
	// ] as const
	const h = 4
	const gaussKernel = computeGaussianKernel(0.75, h)
	const gaussSize = 2 * h + 1 //5
	let sum = 0

	// Apply gauss smooth to input grayscale image to remove noise
	const smoothed = input.slice()
	for (
		let x = Math.floor((gaussSize - 1) / 2);
		x < width - Math.floor((gaussSize + 1) / 2);
		x++
	) {
		for (
			let y = Math.floor((gaussSize - 1) / 2);
			y < height - Math.floor((gaussSize + 1) / 2);
			y++
		) {
			sum = 0
			for (let x1 = 0; x1 < gaussSize; x1++) {
				for (let y1 = 0; y1 < gaussSize; y1++) {
					const x2 = x - Math.floor((gaussSize - 1) / 2) + x1
					const y2 = y - Math.floor((gaussSize - 1) / 2) + y1
					const value =
						(input[y2 * width + x2] ?? 0) * <number>gaussKernel[y1 * gaussSize + x1]
					sum += value
				}
			}
			smoothed[y * width + x] = sum
		}
	}

	// endMeasurement()
	return { data: smoothed, width, height }
}

function getSobelResult(imageData: ArrayData, subsampling: number = 1) {
	const endMeasurement = startMeasurement("getSobelResult")

	let smoothed = gaussian(imageData)
	if (subsampling != 1) {
		const newWidth = Math.floor(imageData.width * subsampling)
		const newHeight = Math.floor(imageData.height * subsampling)

		smoothed = bilinearInterpolation(smoothed, newWidth, newHeight)
	}

	// paper used 2x2 instead of 3x3, seems better for dealing with noise and get longer line
	const result = sobel2x2(smoothed)

	endMeasurement()
	return result
}

export function sobel2x2(inputData: ArrayData) {
	const input = inputData.data
	const width = inputData.width
	const height = inputData.height

	const GY = new Array(width * height).fill(0)
	const GX = new Array(width * height).fill(0)
	const total = new Array(width * height).fill(0)

	// Calculate gradient along x and y
	for (let x = 0; x < width - 1; x++) {
		for (let y = 0; y < height - 1; y++) {
			const index = y * width + x
			const x2 = x + 1
			const y2 = y + 1
			const DA = <number>input[y2 * width + x2] - <number>input[y * width + x]
			const BC = <number>input[y * width + x2] - <number>input[y2 * width + x]
			GX[index] = (DA + BC) / 2 // magnitude 1/4 of old sobel3x3
			GY[index] = (DA - BC) / 2
			total[index] = Math.sqrt(GX[index] * GX[index] + GY[index] * GY[index])
		}
	}

	return { total, GX, GY, width, height }
}

// function sobel3x3(inputData: ArrayData) {
// 	const template = [-1, 0, 1, -2, 0, 2, -1, 0, 1] as const
// 	const templateSize = 3

// 	const input = inputData.data
// 	const width = inputData.width
// 	const height = inputData.height

// 	const GY = new Array(width * height).fill(0)
// 	const GX = new Array(width * height).fill(0)
// 	const total = new Array(width * height).fill(0)
// 	let sum = 0

// 	// Calculate gradient along x and y
// 	for (
// 		let x = Math.floor((templateSize - 1) / 2);
// 		x < width - Math.floor((templateSize + 1) / 2);
// 		x++
// 	) {
// 		for (
// 			let y = Math.floor((templateSize - 1) / 2);
// 			y < height - Math.floor((templateSize + 1) / 2);
// 			y++
// 		) {
// 			sum = 0
// 			for (let x1 = 0; x1 < templateSize; x1++) {
// 				for (let y1 = 0; y1 < templateSize; y1++) {
// 					const x2 = x - Math.floor((templateSize - 1) / 2) + x1
// 					const y2 = y - Math.floor((templateSize - 1) / 2) + y1
// 					const value = input[y2 * width + x2]! * template[y1 * templateSize + x1]!
// 					sum += value
// 				}
// 			}
// 			GX[y * width + x] = sum

// 			sum = 0
// 			for (let x1 = 0; x1 < templateSize; x1++) {
// 				for (let y1 = 0; y1 < templateSize; y1++) {
// 					const x2 = x - Math.floor((templateSize - 1) / 2) + x1
// 					const y2 = y - Math.floor((templateSize - 1) / 2) + y1
// 					const value = input[y2 * width + x2]! * template[x1 * templateSize + y1]!
// 					sum += value
// 				}
// 			}
// 			GY[y * width + x] = sum
// 		}
// 	}

// 	for (let x = 0; x < width; x++) {
// 		for (let y = 0; y < height; y++) {
// 			const norm = Math.sqrt(
// 				GX[y * width + x] * GX[y * width + x] + GY[y * width + x] * GY[y * width + x],
// 			)
// 			total[y * width + x] = Math.trunc(norm)
// 		}
// 	}

// 	return { total, GX, GY, width, height }
// }

// New method:

function getConfidence(l: Line, pointsThresh = 100, widthThresh = 7, intensityThresh = 50) {
	const points = l.points <= 150 ? l.points : 150 + 0.3 * l.points - 45
	const pointsScore = points / pointsThresh - 0.3
	let widthScore = clamp((widthThresh - l.width) / widthThresh, -1, 0.5)
	let intensityScore =
		l.intensity < intensityThresh
			? (l.intensity - intensityThresh) / intensityThresh
			: (l.intensity - intensityThresh) / 150
	intensityScore = clamp(intensityScore, -1, 1.2)
	widthScore = Math.max(widthScore, Math.min(intensityScore * 0.4, intensityScore - 0.5))
	const lengthScore = clamp(Math.min(l.length, l.points * 2) / 300, 0, 1)
	const otherScore = widthScore * 0.2 + intensityScore * 0.5 + lengthScore * 0.3
	const score = pointsScore + otherScore

	// logLineDetection("line scores:", {
	// 	l,
	// 	score,
	// 	pointsScore,
	// 	widthScore,
	// 	intensityScore,
	// })
	return Math.max(score, 0)
}

function getRotationScore(l: Line, rotationScale = 3) {
	return clamp(1 - Math.abs(l.rotation) / rotationScale, -1.7, 0)
}

function getRScore(l: Line, width: number, height: number) {
	let score = 0
	if (l.isVertical) {
		score = 1 - l.combinedR / (width * 0.55)
	} else {
		score = 1 - l.combinedR / (height * 0.55)
	}
	return clamp(score, 0, 1)
}

function getImportanceWeightedRotation(lines: Line[]) {
	let weight = 0
	let aveRotation = 0
	for (const line of lines) {
		aveRotation += line.rotation * <number>line.importance
		weight += <number>line.importance
	}
	return weight === 0 ? 0 : aveRotation / weight
}

// Todo: works well for a few dominant lines; but not so well for too many lines on one side
function getAutoRotationFromDominantLines(
	lines: Line[],
	width: number,
	height: number,
): [number, Line | undefined] {
	const pointsThresh = 85
	const angleThresh = 8
	const usedLines = lines.filter(
		(line) =>
			Math.abs(line.rotation) <= angleThresh &&
			(line.points > pointsThresh ||
				(line.points > pointsThresh - 20 &&
					Math.abs(line.rotation) < 3 &&
					line.combinedR < 70)),
	)
	if (usedLines.length === 0) return [0, undefined]

	for (const line of usedLines) {
		line.confidence = line.confidence ?? getConfidence(line)
		line.rScore = getRScore(line, width, height)
		const confWithRotation = Math.max(0, line.confidence + getRotationScore(line))
		line.importance =
			confWithRotation * line.rScore + line.rScore * 0.1 + confWithRotation * 0.1
	}
	usedLines.sort((a, b) => <number>b.importance - <number>a.importance)
	logLineDetection("used lines", { usedLines })

	// remove rotation that are too different from others (likely outlier like clothes edge)
	let allRotations = usedLines.map((val) => val.rotation)
	let aveRotation = getImportanceWeightedRotation(usedLines)
	let deviations = allRotations.map((val) => Math.abs(val - aveRotation))
	let meanDeviation = getMean(deviations)
	let devThresh = Math.max(meanDeviation * 2, 1.3)
	let filteredLines: Line[] = usedLines.filter((_, i) => (deviations[i] ?? 0) <= devThresh)
	logLineDetection("dev filteredLines", { aveRotation, meanDeviation, filteredLines })

	// filter 2nd time?
	if (filteredLines.length != usedLines.length) {
		allRotations = filteredLines.map((val) => val.rotation)
		aveRotation = getImportanceWeightedRotation(filteredLines)
		deviations = allRotations.map((val) => Math.abs(val - aveRotation))
		meanDeviation = getMean(deviations)
		devThresh = Math.max(meanDeviation * 2, 1.3)
		filteredLines = filteredLines.filter((_, i) => (deviations[i] ?? 0) <= devThresh)

		logLineDetection("2nd dev filteredLines", { aveRotation, meanDeviation, filteredLines })
	}

	// recompute rotation score
	for (const line of filteredLines) {
		let rotationScore =
			Math.max(0, Math.abs(line.rotation - aveRotation) - 1) / clamp(2 * meanDeviation, 3, 10)
		rotationScore = clamp(1 - rotationScore, 0.3, 1)
		line.importance = <number>line.importance * rotationScore
	}
	filteredLines.sort((a, b) => <number>b.importance - <number>a.importance)

	// deduct importance to make really important lines pop
	const deduction = (filteredLines[0]?.importance ?? 0) * 0.35
	for (const line of filteredLines) {
		line.importance = Math.max(0, <number>line.importance - deduction)
	}
	filteredLines = filteredLines.filter((line) => <number>line.importance > 0)

	const meanRotation = getImportanceWeightedRotation(filteredLines)

	const usedLineCount = filteredLines.length
	const maxPoints = Math.max(...filteredLines.map((line) => line.points))
	const minAbsR = Math.min(...filteredLines.map((line) => Math.abs(line.combinedR)))
	const maxWidth = Math.max(...filteredLines.map((line) => Math.abs(line.width)))
	const absRotation = Math.abs(meanRotation)

	// need higher confidence for larger rotation
	if (usedLineCount === 1 || (usedLineCount <= 3 && absRotation >= 3)) {
		const deltaLength = Math.max(absRotation - 1.5, 0) * 10
		if (maxPoints < 65 + deltaLength) return [0, undefined]
		if (maxPoints < 60 + deltaLength + dThresh && (minAbsR > 30 * rScale || maxWidth > 10))
			return [0, undefined]
		if (usedLineCount === 1) {
			if (maxPoints < 90 + deltaLength + dThresh && (minAbsR > 50 * rScale || maxWidth > 8))
				return [0, undefined]

			const filteredLines0 = filteredLines[0] as Line
			if (
				!filteredLines0.isVertical &&
				filteredLines0.length < width * 0.4 &&
				<number>filteredLines0.confidence < 1.8
			)
				return [0, undefined]
		}
	}
	if (absRotation <= 5) return [meanRotation, filteredLines[0]]
	if (absRotation <= 8 && usedLineCount >= 3 && maxPoints >= 90)
		return [meanRotation, filteredLines[0]]

	return [0, undefined]
}

function getPolarCoord(point: vec2, x0: number, y0: number, focalLength: number = 1600): vec2 {
	const x = point[0] - x0
	const y = point[1] - y0
	const z = focalLength
	const phi = Math.acos(z / Math.sqrt(x * x + y * y + z * z)) // [0, pi/2]
	const lambda = Math.atan2(y, x) + Math.PI // [0, 2pi]
	return [phi, lambda]
}

function distanceToEdge(point: vec2, width: number, height: number) {
	const [x, y] = point as [number, number]
	const dx = x > width / 2 ? x - width : -x
	const dy = y > height / 2 ? y - height : -y
	return Math.max(dx, dy)
}

function getVanishingPoints(
	lines: Line[],
	width: number,
	height: number,
	refRotation: number,
	errorThresh = 7,
	rotationThresh = 8,
) {
	const vanishingPoints: VanishingPoint[] = []
	let parallelLines: Line[] = []

	if (lines.length < 3) return { vanishingPoints, parallelLines }

	lines = lines.sort((a, b) => <number>b.confidence - <number>a.confidence)

	if (lines.length > 30) lines = lines.slice(0, 30)

	logLineDetection("getVanishingPoints lines:", { lines })

	const centerX = width / 2
	const centerY = height / 2
	const weights = lines.map((l) => <number>l.confidence + 2)
	const n = lines.length
	const parallelRotations: number[] = []
	const candidateIntersections: VanishingPoint[] = []
	for (let i = 0; i < n; i++) {
		const line = lines[i] as Line
		for (let j = i + 1; j < n; j++) {
			const lineJ = lines[j] as Line
			const interPoint = getIntersection(line.lineEq, lineJ.lineEq)
			if (!interPoint) {
				const rotation = (line.rotation + lineJ.rotation) / 2
				parallelRotations.push(rotation)
				continue
			}
			// exclude inside image or near image point
			const distE = distanceToEdge(interPoint, width, height)
			if (distE < 1500) continue

			const dx = interPoint[0] - centerX
			const dy = interPoint[1] - centerY
			let theta = rad2Degree(Math.atan2(dy, dx))
			if (theta < 0) theta += 180
			const { rotation, isVertical } = thetaToRotation(theta)
			if (!isVertical || Math.abs(rotation) > rotationThresh) continue

			const errors = new Array(n)
			for (let k = 0; k < n; k++) {
				errors[k] = distanceFromPointToLine(interPoint, (<Line>lines[k]).lineEq)
			}
			let sumError = 0
			let weight = 0
			let confidence = 0
			const supportLines: Line[] = []
			for (let k = 0; k < n; k++) {
				if (errors[k] < errorThresh) {
					const lineK = lines[k] as Line
					supportLines.push(lineK)
					confidence += <number>lineK.confidence + 0.5

					if (k != i && k != j) {
						sumError += errors[k] * <number>weights[k]
						weight += weights[k] as number
					}
				}
			}
			if (supportLines.length <= 2) continue
			const aveError = sumError / weight
			const rotationDiff = Math.abs(rotation - refRotation)
			const Xs = supportLines.map((l) => l.endPointsOnBoundary[0])
			const stdX = getStandardDeviation(Xs) / width
			// logLineDetection("intersection:", {i, j, confidence, aveError, rotationDiff, stdX })

			confidence += -aveError * 0.7 - rotationDiff * rotationDiff + stdX * 8
			if (distE < 4000) confidence -= (4000 - distE) / 1000
			if (aveError < 1 && supportLines.length >= 4) confidence += (1 - aveError) * 2
			if (aveError < 1.5 && distE > 8000) confidence += Math.min((distE - 8000) / 4000, 2)

			const intersection: VanishingPoint = {
				point: interPoint,
				rotation: rotation,
				lines: supportLines,
				aveError: aveError,
				errors: errors,
				confidence: confidence,
				stdX: stdX,
				polarCoord: getPolarCoord(interPoint, centerX, centerY),
				distE: distE,
			}

			candidateIntersections.push(intersection)
		}
	}

	candidateIntersections.sort((a, b) => b.confidence - a.confidence)
	// logLineDetection("getVanishingPoints candidateIntersections", { candidateIntersections })

	const maxConfidence = candidateIntersections[0]?.confidence ?? 0
	for (const vp of candidateIntersections) {
		if (vp.stdX < 0.06) continue

		const rotationDiff = Math.abs(vp.rotation - refRotation)
		const notCheckStd =
			(vp.lines[1]?.points ?? 0) > 105 &&
			(vp.lines[2]?.points ?? 0) > 105 &&
			rotationDiff < 0.5 &&
			vp.aveError < 3
		if (!notCheckStd) {
			if (vp.stdX < 0.18) continue
			if (vp.stdX < 0.24 && vp.lines.length <= 4) continue
			if (vp.stdX < 0.21 && vp.lines.length <= 7 && vp.distE < 4000) continue
		}

		if (vanishingPoints.length >= 2 && vp.confidence < maxConfidence - 6) break

		let foundSamePoint = false
		for (const prevVP of vanishingPoints) {
			const diffPhi = rad2Degree(angleDiff(vp.polarCoord[0], prevVP.polarCoord[0]))
			const diffLambda = rad2Degree(angleDiff(vp.polarCoord[1], prevVP.polarCoord[1]))
			if (diffPhi < 2 && diffLambda < 2) {
				foundSamePoint = true
				break
			}
		}
		if (!foundSamePoint) vanishingPoints.push(vp)
	}

	if (parallelRotations.length > 0) {
		let maxCount = 0
		for (const rotation of parallelRotations) {
			const filteredLines = lines.filter((l) => Math.abs(l.rotation - rotation) < 0.2)
			if (filteredLines.length > maxCount) {
				maxCount = filteredLines.length
				parallelLines = filteredLines
			}
		}
		parallelLines.sort((a, b) => <number>b.confidence - <number>a.confidence)
	}

	return { vanishingPoints, parallelLines }
}

function getXRotation(y, height) {
	let sign = 1
	if (y < 0) {
		y = -y + height
		sign = -1
	}
	// 3D rotation about X to make the inter-point (x1, y1) between vp-leftlower and top-edge to vertical
	const y1 = -1 // when z = 1; actual coord: -height / 2
	const lambda = (y - height) / (y - height / 2) // x1 / x1'
	const c = 1 / Math.sqrt(y1 * y1 + 1)
	const xRotation = rad2Degree((Math.asin(lambda * c) - Math.asin(c)) * sign)

	return xRotation
}

function getXRotationFromLine(line: Line, autoAngle, width, height, pointsT1 = 85, pointsT2 = 65) {
	if (line.points > pointsT1 || (<number>line.confidence > 0.6 && line.points > pointsT2)) {
		const origin: [number, number] = [width / 2, height / 2]

		const [x1, y1, x2, y2] = line.endPointsOnBoundary as [number, number, number, number]
		const [x1r, y1r] = rotatePoint(x1, y1, radians(autoAngle), origin) as [number, number]
		const [x2r, y2r] = rotatePoint(x2, y2, radians(autoAngle), origin) as [number, number]
		const [A, B, C] = getLineEquation([x1r, y1r, x2r, y2r]) as [number, number, number] // ax + by + c = 0

		const y = (-C - (A * width) / 2) / B

		return getXRotation(y, height)
	} else {
		return null
	}
}

function getXRotationFromVerticalEdgeLines(lines: Line[], autoAngle, width, height) {
	const leftLines = lines.filter(
		(l) => (l.endPointsOnBoundary[0] + l.endPointsOnBoundary[2]) / 2 < width / 3,
	)
	leftLines.sort(
		(a, b) => <number>b.confidence + b.points / 100 - <number>a.confidence - a.points / 100,
	)
	const leftLine = leftLines[0]

	const rightLines = lines.filter(
		(l) => (l.endPointsOnBoundary[0] + l.endPointsOnBoundary[2]) / 2 > (width * 2) / 3,
	)
	rightLines.sort(
		(a, b) => <number>b.confidence + b.points / 100 - <number>a.confidence - a.points / 100,
	)
	const rightLine = rightLines[0]

	const angleLeft = leftLine ? getXRotationFromLine(leftLine, autoAngle, width, height) : null
	const angleRight = rightLine ? getXRotationFromLine(rightLine, autoAngle, width, height) : null
	logLineDetection("angleLeft, right", { leftLine, angleLeft, rightLine, angleRight })

	let xRotation = 0
	if (angleLeft != null && angleRight != null && leftLine && rightLine) {
		const angleDiff = Math.abs(angleLeft - angleRight)
		if (angleLeft * angleRight >= 0 && angleDiff < 1.5) {
			xRotation =
				angleLeft * <number>leftLine.confidence + angleRight * <number>rightLine.confidence
			xRotation /= Math.max(
				0.0001,
				<number>leftLine.confidence + <number>rightLine.confidence,
			)

			if (angleDiff > 1) {
				const scaleDown = 1.0 - (angleDiff - 1) * 0.6
				xRotation *= scaleDown
				logLineDetection("xRotation from edge lines scaleDown:", { scaleDown })
			}
		}
	}
	return xRotation
}

function isLineHorizontalOrIncluded(lines: Line[], dominantLine: Line) {
	if (!dominantLine.isVertical) return true
	for (const l of lines) {
		if (l.rotation === dominantLine.rotation && l.r === dominantLine.r) {
			return true
		}
	}
	logLineDetection("dominant line not included in VP", { lines, dominantLine })
	return false
}

function getParallelLinesInfo(parallelLines: Line[], dominantAngle: number, width: number) {
	let parallelConfidence = 0
	let parallelRotationDiff = 0
	let parallelStdX = 0
	if (parallelLines.length > 0) {
		for (const line of parallelLines) {
			parallelConfidence += (line.confidence ?? 0) + 0.5
		}
		const Xs = parallelLines.map((l) => l.endPointsOnBoundary[0])
		parallelStdX = getStandardDeviation(Xs) / width

		const aveRotation = getMean(parallelLines.map((l) => l.rotation))
		parallelRotationDiff = Math.abs(aveRotation - dominantAngle)

		parallelConfidence += -parallelRotationDiff * parallelRotationDiff + parallelStdX * 8

		logLineDetection("parallelConfidence:", {
			parallelConfidence,
			aveRotation,
			dominantAngle,
			parallelStdX,
		})
	}

	return { parallelConfidence, parallelRotationDiff, parallelStdX }
}

function getAutoPerspectiveCorrection(lines: Line[], width: number, height: number) {
	for (const line of lines) {
		line.confidence = getConfidence(line)
	}
	const [dominantAngle, dominantLine] = getAutoRotationFromDominantLines(lines, width, height)
	let autoAngle = dominantAngle
	const pointsThresh = height > 450 ? 75 : 60
	const verticalLines = lines.filter(
		(l) => l.isVertical && Math.abs(l.rotation) < 10 && l.points >= pointsThresh,
	)

	// Todo: distortion for horizontal lines
	// const horizontalLines = lines.filter(l => !l.isVertical)

	const { vanishingPoints, parallelLines } = getVanishingPoints(
		verticalLines,
		width,
		height,
		dominantAngle,
	)
	logLineDetection("Vertical:", { vanishingPoints, parallelLines })

	const { parallelConfidence, parallelRotationDiff, parallelStdX } = getParallelLinesInfo(
		parallelLines,
		dominantAngle,
		width,
	)

	let vp1: VanishingPoint | null = null
	let useVP1ForAngle = false
	let dt = 0
	if (vanishingPoints.length > 0) {
		for (const vp of vanishingPoints) {
			const rotationDiff = Math.abs(vp.rotation - dominantAngle)
			const dominantRScore = dominantLine?.rScore ?? 0
			const dominantConf = dominantLine?.confidence ?? 0
			if (
				rotationDiff < 0.5 ||
				(dominantLine &&
					rotationDiff < 1 &&
					isLineHorizontalOrIncluded(vp.lines, dominantLine)) ||
				vp.confidence >
					6 + rotationDiff * dominantConf * dominantRScore * dominantRScore * 3 ||
				(dominantRScore < 0.5 && vp.confidence > 3.5 + Math.abs(vp.rotation))
			) {
				vp1 = vp
				vp1.lines.sort((a, b) => <number>b.confidence - <number>a.confidence)
				break
			}
		}

		if (
			(!vp1 || parallelConfidence > Math.min(vp1.confidence * 2, vp1.confidence + 5)) &&
			parallelConfidence > 5 &&
			parallelStdX > 0.06 &&
			parallelRotationDiff < 0.5
		) {
			logLineDetection("Use parallel lines")
			return { cropStraighten: toFixed(autoAngle, 2), perspectiveVertical: 0 }
		}

		if (vp1 && vp1.lines.length >= 3 && vp1.confidence > 2) {
			const vp1Line1 = vp1.lines[1] as Line
			const vp1Line2 = vp1.lines[2] as Line
			const top23LineConf = <number>vp1Line1.confidence + <number>vp1Line2.confidence
			const top23Points = (vp1Line1.points + vp1Line2.points) / 2
			const pointsThresh = Math.max(80, (dominantLine?.points ?? 0) * 0.5)
			dt = Math.max(0, 4000 - vp1.distE) / 1000
			logLineDetection("Use vp for angle??", { top23LineConf, top23Points })
			if (
				vp1.confidence > 9 ||
				(vp1.confidence > 3 + dt &&
					top23Points > pointsThresh &&
					((vp1.lines.length >= 4 && vp1.aveError < 3.2) || top23LineConf > 2))
			) {
				autoAngle = vp1.rotation
				useVP1ForAngle = true
				logLineDetection("Use vp for angle:", { dominantAngle, autoAngle, dominantLine })
			}
		}
	}
	if (!useVP1ForAngle) logLineDetection("Use dominant angle:", { dominantAngle, dominantLine })
	logLineDetection("vp1", { vp1 })

	// vertical upright
	let xRotation = 0
	let error = 0
	if (
		vp1 &&
		vp1.confidence > 3 + dt &&
		(useVP1ForAngle ||
			(Math.abs(vp1.rotation - autoAngle) < 0.8 &&
				vp1.stdX > 0.25 &&
				(vp1.lines[0]?.confidence ?? 0) > 0.8))
	) {
		// connect vp to image edge point and make them vertical
		let [x, y] = vp1.point
		if (autoAngle != 0) {
			;[x, y] = rotatePoint(x, y, radians(vp1.rotation), [width / 2, height / 2])
		}
		logLineDetection("rotated VP1 [x, y]:", x, y)

		xRotation = getXRotation(y, height)
		const rotationDiff1 = Math.abs(vp1.rotation - autoAngle)
		if (rotationDiff1 > 0.4) {
			const scaleDown = 1.4 - rotationDiff1
			xRotation *= scaleDown
			logLineDetection("xRotation from VP1 scaleDown:", { scaleDown })
		}
		error = vp1.aveError - (vp1.confidence - 3 - dt) * 0.3
		logLineDetection("xRotation from VP1:", { xRotation })
	} else {
		// find long lines near edge and make them vertical
		xRotation = getXRotationFromVerticalEdgeLines(verticalLines, autoAngle, width, height)
		if (xRotation) {
			logLineDetection("xRotation from edge lines:", { xRotation })
		}
	}
	let perspectiveVertical = (xRotation * 2) / -45
	if (Math.abs(perspectiveVertical) >= 0.3 && error > 1.5) perspectiveVertical = 0
	if (Math.abs(perspectiveVertical) >= 0.5) perspectiveVertical = 0

	// angle positive = clockwise in both UI and math here
	return {
		cropStraighten: toFixed(autoAngle, 2),
		perspectiveVertical: toFixed(perspectiveVertical, 2),
	}
}

function detectLinesViaLSD(imageData: ArrayData) {
	const subsampling = 0.8
	const { total, GX, GY, width, height } = getSobelResult(imageData, subsampling)
	const rects = detectLineSegmentRects(total, GX, GY, width, height, subsampling)
	const lineSegments = getLineSegments(rects, imageData.width, imageData.height, 15)
	const lines = getCombinedLines(lineSegments, 60) // 40

	// logLineDetection("LSD lines:", { lineSegments, lines })
	return lines
}

export function getAutoCorrection(imageData: PixelData): Partial<Adjustments> {
	const endMeasurement = startMeasurement("AIWorker: getAutoCorrection")

	const grayImage = grayScale(imageData)
	const lines = detectLinesViaLSD(grayImage)

	// const angle = getAutoRotationFromDominantLines(lines, imageData.width, imageData.height)
	const correction: Partial<Adjustments> = getAutoPerspectiveCorrection(
		lines,
		imageData.width,
		imageData.height,
	)
	logLineDetection("getAutoCorrection:", { correction })

	endMeasurement()

	return correction
}
